import queryString from "query-string";
import { debug, setDebug } from "rtUtils/logger";
import createPostMessageClient from "rtUtils/postMessageClient";
import { APP_EVENTS, TAG_EVENTS } from "shared/events";

const qs = queryString.parse(location.search)
const parentOrigin = qs.origin;
const viewerId = qs.viewerId;

const LISTENERS_KEY = "_listeners";

//singleton for the entire app
let storeInstance;

const findInConfig = (parent, path) => {
	const find = (parent, paths) => {
		const name = paths.splice(0, 1)[0];
		const child = parent[name];
		return paths.length ? (child ? find(child, paths) : undefined): child;
	};

	return find(parent,
		Array.isArray(path) ? [].concat(path) : path.split("."));
};

const createPostMessageService = () => {
	let isInitialized = false;
	let initPromise;
	let renderApi;

	if (!storeInstance) {
		const state = {
			eventHandlers: new Map(),
			config: null,
			updates: 0,
		};

		const emit = (type) => {
			const listeners = state.eventHandlers.get(type);
			listeners.forEach((listener) => listener());
		};

		const setConfig = (pmConfig) => {
			state.config = pmConfig;
			emit(LISTENERS_KEY);
		};

		const addUpdate = () => {
			state.updates += 1;
		};

		const init = () => {
			initPromise = new Promise((resolve) => {
				const pmClient = createPostMessageClient(parentOrigin,
					{
						[TAG_EVENTS.INIT]: (data) => {
							if (data?.config?.debug === true) {
								setDebug(true);
							}

							debug("APP RECEIVED INIT: ", { data, state });
							setConfig(data.config);
						},

						[TAG_EVENTS.UPDATE]: (data) => {
							debug("APP RECEIVED UPDATE: ", data);
							addUpdate();
							setConfig(data.config);
						},

						[TAG_EVENTS.TERMINATE]: () => {
							debug("APP RECEIVED TERMINATE !!!");
							setConfig(null);
							terminate();
						},

						[TAG_EVENTS.PRINT_STRUCTURE]: () => {
							debug("APP RECEIVED `PRINT_STRUCTURE` EVENT", { apiReady: !!renderApi });
							if (renderApi) {
								renderApi.printSceneStructure();
							}
						},

						[TAG_EVENTS.EXPORT_GLTF]: async (data) => {
							data = data || {};
							debug("APP RECEIVED `EXPORT_GLTF` EVENT", { data, apiReady: !!renderApi });
							if (renderApi) {
								const gltf = await renderApi.exportGltf(...data.params);
								const blob = new Blob([gltf], {
									type: "model/gltf-binary",
								});

								debug("exported gltf: ", gltf);

								if (data.download) {
									const a = document.createElement("a");
									const objectUrl = URL.createObjectURL(blob);
									a.setAttribute("download", `model-${Date.now()}.glb`);
									a.setAttribute("href", objectUrl);
									document.body.appendChild(a);
									a.click();
									document.body.removeChild(a);
									URL.revokeObjectURL(objectUrl);
								}
							}
						},

						[TAG_EVENTS.CLOSE_AR_QR_MODAL]: () => {
							debug("APP RECEIVED `CLOSE_AR_QR_MODAL` EVENT");
							emit(TAG_EVENTS.CLOSE_AR_QR_MODAL);
						},

						[TAG_EVENTS.GET_MODEL_INFO]: () => {
							const apiReady = !!renderApi
							const info = renderApi?.getGltfInfo();
							debug("APP RECEIVED `GET_MODEL_INFO` EVENT", { apiReady, info });
							pmClient.send(APP_EVENTS.MODEL_INFO, { info, apiReady });
						},
					},
					window.parent,
				);

				const pmSend = pmClient.send;
				pmClient.send = (type, data = {}) => {
					pmSend(type, {
						...data,
						viewerId,
					});
				};

				isInitialized = true;
				pmClient.send(APP_EVENTS.READY, { appUrl: location.toString() });
				resolve(pmClient);
			});
		};

		const triggerEvent = (eventName, data = {}) => {
			initPromise.then((pmClient) =>
				pmClient.send(eventName, data));
		};

		const terminate = () => {
			state.eventHandlers.clear();

			initPromise.then((pmClient) => {
				pmClient.close();
				isInitialized = false;
			});
		};

		const setRendererApi = (rApiInstance) => {
			renderApi = rApiInstance;
			if (renderApi) {
				initPromise.then((pmClient) => {
					pmClient.send(APP_EVENTS.RENDERING_API_READY, {});
				});
			}
		}

		const addHandler = (type, cb) => {
			if (!state.eventHandlers.has(type)) {
				state.eventHandlers.set(type, new Set([cb]));
			} else {
				const handlers = state.eventHandlers.get(type);
				if (!handlers.has(cb)) {
					handlers.add(cb);
				}
			}

			return () => {
				if (state.eventHandlers.has(type)) {
					const handlers = state.eventHandlers.get(type);
					if (handlers.has(cb)) {
						handlers.delete(cb);

						if (handlers.size === 0) {
							state.eventHandlers.delete(type);
						}
					}
				}
			};
		};

		storeInstance = {
			init,
			terminate,
			triggerEvent,
			setRendererApi,

			get isInitialized () {
				return isInitialized;
			},

			addHandler,

 			subscribe: (listener) =>
			  //we treat listeners as a type of handler to keep things simple
			  addHandler(LISTENERS_KEY, listener),

			getHasConfig: () => {
				return !!state.config;
			},

			getUpdateCount: () => {
				return state.updates;
			},

			//TODO: consider making state immutable so it cannot be changed from outside (readonly)
			createSelector: (selector) => () =>
				typeof selector === "function" ?
					selector(state.config) :
					(state.config ? findInConfig(state.config, selector) : null),

			findInConfig,
		};
	}

	return storeInstance;
};

export default createPostMessageService;
